<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { auth0 } from '@/utils/auth0';
import * as jose from 'jose';

import DSButton from 'primevue/button';
import Divider from 'primevue/divider';

// Types
interface NewIdentity {
  user_id: string;
  email: string;
  provider: string;
  connection: string;
}

interface CandidateIdentity {
  user_id: string;
  provider: string;
  connection: string;
  clearco_id: string;
}

interface JwtPayload {
  new_identity_to_link: NewIdentity;
  candidate_identities: CandidateIdentity[];
}

// Props
const props = defineProps<{
  linkAccountToken?: string | null;
}>();

// Reactive state
const newIdentity = ref<NewIdentity | null>(null);
const candidateIdentities = ref<CandidateIdentity[]>([]);

// Methods
const linkAccount = (identity: CandidateIdentity): void => {
  if (!props.linkAccountToken) {
    throw new Error('Link account token is not available');
  }

  auth0.loginWithRedirect({
    authorizationParams: {
      connection: identity.connection,
      link_account_token: props.linkAccountToken,
    },
  });
};

// Lifecycle
onMounted(async () => {
  if (!props.linkAccountToken) {
    throw new Error('link_account_token is required');
  }

  const payload = (await jose.decodeJwt(props.linkAccountToken)) as JwtPayload;

  newIdentity.value = payload.new_identity_to_link;
  candidateIdentities.value = payload.candidate_identities;
});
</script>

<template>
  <div class="wrapper">
    <h1 class="heading">Link Accounts</h1>
    <Divider />
    <h4 class="heading">Accounts with the same email address</h4>
    <p>
      We noticed there are other registered users with the same email address:
      <b>{{ newIdentity?.email }}</b
      >. Do you want to link the accounts?
    </p>
    <table class="table">
      <thead>
        <tr>
          <th>Clearco ID</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="identity in candidateIdentities" :key="identity.user_id">
          <td>{{ identity.clearco_id }}</td>
          <td>
            <DSButton
              label="Link"
              @click="linkAccount(identity)"
              class="p-button-sm"
            ></DSButton>
          </td>
        </tr>
      </tbody>
    </table>
    <p>
      You will not be able to proceed without linking accounts as we disallow
      multiple users with the same email. <a href="/logout">Click here</a> to
      return to the login page.
    </p>
  </div>
</template>

<style lang="less" scoped>
.wrapper {
  padding: 1rem;
  max-width: 600px;
}

.heading {
  margin: 0;
  line-height: 1;
}

.table {
  text-align: center;
}

th {
  background-color: rgb(234, 234, 234);
}

th,
td {
  padding: 0.5rem 1rem;
}

.link-account-button {
  font-size: 1rem;
  padding: 0.2rem 0.5rem;
  margin: 0.5rem;
}
</style>
